import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  // Reset scroll to the top SYNCHRONOUSLY, before the browser paints the new
  // page. Doing this in useEffect + requestAnimationFrame (the old approach) let
  // the new page paint ONCE at the previous scroll offset — a one-frame flash of
  // mid-page content that read as a "blink/flicker" when changing pages. A layout
  // effect runs after the DOM commit but before paint, so the new route is never
  // shown at the old scroll position.
  useLayoutEffect(() => {
    if (hash) return; // hash navigations scroll to an in-page target instead (below)

    // behavior:'instant' forces an immediate jump even though the page sets a
    // global `scroll-behavior: smooth` (which would otherwise animate the
    // scroll-to-top and is itself part of the visible flicker on navigation).
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  // Hash navigation: SMOOTH-scroll to the in-page target once it has rendered, so
  // an anchor button (e.g. the hero "Se långivare" → #comparison-section) glides
  // down the same way the "Jämför långivare" button on lån-utan-UC does, instead
  // of jumping there abruptly.
  useEffect(() => {
    if (!hash) return;

    const timeouts: Array<ReturnType<typeof setTimeout>> = [];

    const scrollToHashTarget = () => {
      const targetId = decodeURIComponent(hash.slice(1));
      const target = document.getElementById(targetId);
      if (!target) return false;

      const navOffset = 80;
      const targetPosition = Math.max(target.getBoundingClientRect().top + window.scrollY - navOffset, 0);
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      return true;
    };

    if (!scrollToHashTarget()) {
      // Target may not exist yet (async content) — retry a couple of times.
      [100, 250].forEach((delay) => timeouts.push(setTimeout(scrollToHashTarget, delay)));
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [pathname, hash]);

  return null;
};
